import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useParameterStore } from '@/frontend/store/ParameterStore';
import { useProcedureInstanceStore } from '@/frontend/store/ProcedureInstanceStore';
import { I18n } from '@/i18n/I18n';
import { ParameterSubjectType, ParameterType } from '@/types/parameterTypes';
import { useShallow } from 'zustand/react/shallow';
import { LoadingSpinner } from '../LoadingSpinner';
import { ProcedurePlaceholder } from '../containers/ContainersPlaceholder';
import { useProcedureListStore } from '@/frontend/store/ProcedureListStore';
import { ProcedureInstance } from '@/backend/models/procedureInstances/procedureInstance';
import { Form } from 'react-bootstrap';
import { useFunctionalInterfacesStore } from '@/frontend/store/FunctionalInterfaceStore';
import { ProcedureCreateParameters } from './ProcedureCreateParameters';
import { NotFound } from '@/frontend/pages/NotFound';

export function ProcedureCreateLoader() {
  const { procedureId } = useParams();
  const procedure = useProcedureInstanceStore((state) => state.procedureInstance);
  const fetchProcedure = useProcedureInstanceStore((state) => state.fetchProcedureInstance);
  const getParametersForSubject = useParameterStore((state) => state.getParametersForSubject);
  const resetProcedure = useProcedureInstanceStore((state) => state.reset);
  const resetParameters = useParameterStore((state) => state.reset);
  const readVersionsForProcedure = useFunctionalInterfacesStore((state) => state.readVersionsForProcedure);
  const resetFunctionalInterfaces = useFunctionalInterfacesStore((state) => state.reset);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (procedureId) {
      const fetch = async () => {
        setLoading(true);
        await Promise.all([fetchProcedure(procedureId), getParametersForSubject(procedureId, ParameterSubjectType.PROCEDURE)]);
        setLoading(false);
      };

      void fetch();
    }

    return () => {
      resetProcedure();
      resetParameters();
    };
  }, [procedureId, fetchProcedure, getParametersForSubject, resetProcedure, resetParameters]);

  useEffect(() => {
    if (procedure?.procedureTemplateId) {
      void readVersionsForProcedure(procedure.procedureTemplateId);
    }

    return () => {
      resetFunctionalInterfaces();
    };
  }, [procedure?.procedureTemplateId]);

  if (loading) {
    return (<ProcedurePlaceholder />);
  }

  if (!procedure) {
    return <NotFound />;
  }

  return (<ProcedureCreate procedure={procedure} />);
}

export function ProcedureCreate(props: { procedure: ProcedureInstance }) {
  const { procedure } = props;
  const createProcedureFromTemplate = useProcedureListStore((state) => state.createProcedureFromTemplate);
  const updateProcedure = useProcedureInstanceStore((state) => state.updateProcedureInstance);
  const navigate = useNavigate();
  const functionalInterfaceVersions = useFunctionalInterfacesStore(useShallow(state => state.functionalInterfaceVersions));

  const nextStepLabel = functionalInterfaceVersions.length > 0 ? I18n.get('continueToImplementationSetup') : I18n.get('createProcedure');

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(procedure.title);

  const onClickCreate = async () => {
    setLoading(true);
    
    if (functionalInterfaceVersions.length === 0) {
      void createProcedureFromTemplate(procedure, {}).then(async (response) => {
        if (response?.status === 200) {
          if (procedure.projectId) {
            void navigate(`/projects/${procedure.projectId}/templates`);
          } else {
            void navigate(`/`);
          }
        }
        setLoading(false);
      });
    } else {
      void navigate(`/procedureImplementationSetup/${procedure.id}`);
    }
  };

 const onUpdateTitle = (value: string) => {
    if(!value) {
      setError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }
    setError('');
    void updateProcedure({ procedureInstance: { ...procedure, title: value } });
  };

  return (
    <div className='card-body p-3 overflow-auto' style={{ maxHeight: '100vh', height: '100vh' }}>
      <h2>{I18n.get('configureProcedure')}</h2>
      <div className='p-3'>
        <div className='mb-3' style={{ maxWidth: '720px' }}>
          <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
          <Form.Control
            size='sm'
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => onUpdateTitle(e.target.value)}
            isInvalid={!!error}
          />
          <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        </div>
        <ProcedureCreateParameters subjectId={procedure.id} type={ParameterType.CONFIG} />
        <div className='d-flex justify-content-end mt-3'>
          <button className='btn btn-sm btn-primary' type='button' onClick={() => void onClickCreate()}>
            { loading ? <LoadingSpinner/> : <span>{nextStepLabel}</span> }
          </button>
        </div>
      </div>
    </div>
  );
}
